// Globals
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

// Store
import { useAppState } from '@/store';
import { propertiesSelector } from '../../feature';

// Modules
import { LogsListContainer } from '@/modules/Logs';
import { PicturesContainer } from '@/modules/Pictures';
import { DocumentsManagementContainer } from '@/modules/PropertyDocuments';
import { PropertyProgressContainer, PropertyTabs } from '@/modules/PropertyProgress';
import { BidsListContainer } from '@/modules/Bidding';

// Models
import { EntityType } from '@/models/logs.model';

// Components
import { StyledTabs } from '../../components';
import OwnerAndPropertyContainer from '../OwnerAndPropertyContainer/OwnerAndPropertyContainer';

type TPropertyDetailsContainerProps = {
  hideResetButton?: boolean;
  isSold?: boolean;
};

const PropertyDetailsContainer = ({ hideResetButton = false, isSold = false }: TPropertyDetailsContainerProps) => {
  const { t } = useTranslation('property');
  const { property } = useAppState(propertiesSelector);
  const [queryParams, setQueryParams] = useSearchParams();

  const handleChangeTab = (key: string) => {
    setQueryParams((prev) => {
      prev.set('tab', key);
      return prev;
    });
  };

  return (
    <StyledTabs
      destroyInactiveTabPane
      onChange={handleChangeTab}
      activeKey={queryParams.get('tab') ?? PropertyTabs.OWNERS}
      items={[
        {
          key: PropertyTabs.OWNERS,
          label: t('property_tabs_owners'),
          children: <OwnerAndPropertyContainer />,
        },
        {
          key: PropertyTabs.PROGRESS,
          label: t('property_tabs_progress'),
          children: <PropertyProgressContainer hideResetButton={hideResetButton} />,
        },
        {
          key: PropertyTabs.BIDDING,
          label: t('property_tabs_bidding'),
          children: <BidsListContainer />,
        },
        {
          key: PropertyTabs.DOCUMENTS,
          label: t('property_tabs_documents'),
          children: <DocumentsManagementContainer uploadHidden={isSold} disabled={isSold} />,
        },
        {
          key: PropertyTabs.PICTURES,
          label: t('property_tabs_pictures'),
          children: <PicturesContainer propertyId={property?.PK ?? ''} disabled={isSold} />,
        },
        {
          key: PropertyTabs.ORDERS,
          label: t('property_tabs_orders'),
          children: <LogsListContainer id={property?.PK ?? ''} entityType={EntityType.PROPERTY} />,
        },
      ]}
    />
  );
};

export default PropertyDetailsContainer;
